/*
 * The boost animation in the Boost desk (ENG-814, FX-7; backend ENG-813 and
 * ENG-815).
 *
 * A boost animation is a skins-catalog item of type `boost_animation` whose
 * `fxPackageId` names the SongShake FX `boost` package it plays. The
 * listener's owned ones are the shelf's `skins` of that type
 * (`GET /me/skins/shelf?category=all`), the free default SongShake Boost
 * (source `default`) always among them, and `equipped.boost_animation` is the
 * one worn.
 *
 * Equipping is `PUT /me/skins/equipped/boost_animation` with `{ skinId }` (the
 * item id, not the package id). It is saved on the account: from then on the
 * server names it as `fx_boost.boostId` on this listener's boosts, so every
 * viewer sees it. No frame follows an equip; the desk takes the 2xx as the
 * new state.
 */
// Through the accessor every test mock of fetchWrapper.js provides.
import { useFetchWrapper as getFetchWrapper } from './fetchWrapper.js';
import config from '../config.js';

/** The equip slot (and item type) of a boost animation. */
export const BOOST_ANIMATION_SLOT = 'boost_animation';

const isId = (x) => typeof x === 'string' && x.trim().length > 0;

/** One owned shelf entry as `{ id, name, fxPackageId, isDefault }`, or null when it is not a boost animation. */
export const normalizeBoostAnimation = (owned) => {
  const skin = owned?.skin;
  if (!skin || skin.type !== BOOST_ANIMATION_SLOT || !isId(skin.id) || !isId(skin.fxPackageId)) return null;
  return {
    id: skin.id,
    name: isId(skin.name) ? skin.name : skin.id,
    fxPackageId: skin.fxPackageId,
    isDefault: owned.source === 'default',
  };
};

/**
 * The boost animations in a raw `SkinShelf` payload: `{ items, equippedId }`,
 * the default first and the rest in the shelf's order. With nothing in the
 * slot the default is the one worn.
 */
export const boostAnimationsFromShelf = (shelf) => {
  const seen = new Set();
  const items = (Array.isArray(shelf?.skins) ? shelf.skins : [])
    .map(normalizeBoostAnimation)
    .filter((item) => item && !seen.has(item.id) && seen.add(item.id));
  items.sort((a, b) => Number(b.isDefault) - Number(a.isDefault));
  const slot = shelf?.equipped?.[BOOST_ANIMATION_SLOT];
  const equipped = items.find((item) => item.id === slot) ?? items.find((item) => item.isDefault) ?? null;
  return { items, equippedId: equipped?.id ?? null };
};

/** Reads the listener's boost animations. Rejects with the fetchWrapper error (401 without a session). */
export const fetchBoostAnimations = async () => {
  const shelf = await getFetchWrapper().get(`${config.apiBaseUrl}/me/skins/shelf?category=all`);
  if (!shelf || !Array.isArray(shelf.skins)) {
    return Promise.reject({ status: 500, code: null, message: 'malformed skin shelf' });
  }
  return boostAnimationsFromShelf(shelf);
};

/** Equips the boost animation with item id `skinId`. Rejects with the fetchWrapper error. */
export const equipBoostAnimation = (skinId) => {
  if (!isId(skinId)) return Promise.reject({ status: 400, code: 'invalid_request' });
  return getFetchWrapper().put(`${config.apiBaseUrl}/me/skins/equipped/${BOOST_ANIMATION_SLOT}`, { skinId });
};

/** Equip error codes with their own message (`stream.boost.desk.animation.error.*`). */
const EQUIP_ERRORS = { skin_not_owned: 'notOwned', skin_not_found: 'notFound' };

/** The catalog key for an equip error. */
export const boostAnimationError = (error) => EQUIP_ERRORS[error?.code] ?? 'generic';
